import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../comps/Icon';
import { useI18n } from '../../i18n/I18nContext';
import '../../style/css/discoversearch.css';

/*
 * DiscoverSearch — a rich, ontopo-style reservation search bar (RTL, Hebrew).
 * One horizontal bar, left→right: CITY picker (modal) · MODE toggle · TIME · DATE · PARTY · SUBMIT.
 *
 * Props:
 *   cities   : string[]  — distinct city names to offer in the picker (default []).
 *   initial  : { city, mode, time, date, party } — seed values (all optional).
 * On submit it navigates to /discover?city=&mode=&time=&date=&party= (city omitted when "כל הערים").
 * Works rendered on ANY page — it always routes to /discover.
 */

// Internal sentinel for "all cities" — a locale-independent value used for state, the URL param
// guard, and equality checks. Its LABEL is translated (t('search.all_cities')).
const ALL_CITIES = '__ALL_CITIES__';
const MODE_BOOK = 'book';        // "הזמנת מקום"
const MODE_AVAIL = 'available';  // "על בסיס מקום פנוי"
const DEFAULT_TIME = '18:00';
const DEFAULT_PARTY = 2;

// Weekday translation keys, Sunday-first (matches Date.getDay()).
const DAY_KEYS = ['search.day_sunday', 'search.day_monday', 'search.day_tuesday', 'search.day_wednesday', 'search.day_thursday', 'search.day_friday', 'search.day_saturday'];

// 10:00 … 23:30 in 30-min steps.
function buildTimes() {
    const out = [];
    for (let h = 10; h <= 23; h += 1) {
        for (let m = 0; m < 60; m += 30) {
            out.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
        }
    }
    return out;
}

// Local ISO date (YYYY-MM-DD) — avoids UTC off-by-one from toISOString().
function isoDate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

// Next ~14 days: today, tomorrow, then "<weekday> DD/MM". Weekday label is localized via `t`.
function buildDates(t) {
    const out = [];
    const base = new Date();
    base.setHours(0, 0, 0, 0);
    for (let i = 0; i < 14; i += 1) {
        const d = new Date(base);
        d.setDate(base.getDate() + i);
        let label;
        if (i === 0) label = t('common.today');
        else if (i === 1) label = t('common.tomorrow');
        else {
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            label = `${t(DAY_KEYS[d.getDay()])} ${dd}/${mm}`;
        }
        out.push({ value: isoDate(d), label });
    }
    return out;
}

function partyLabel(n, t) {
    return n === 1 ? t('search.party_one') : t('search.party_many', { count: n });
}

export default function DiscoverSearch({ cities = [], initial = {} }) {
    const navigate = useNavigate();
    const { t } = useI18n();

    const times = useMemo(buildTimes, []);
    const dates = useMemo(() => buildDates(t), [t]);
    const parties = useMemo(() => Array.from({ length: 20 }, (_, i) => i + 1), []);

    // Sorted, de-duped, non-empty city list (he-localeCompare).
    const cityOptions = useMemo(() => {
        const set = new Set();
        (Array.isArray(cities) ? cities : []).forEach((c) => {
            const s = (c || '').trim();
            if (s) set.add(s);
        });
        return Array.from(set).sort((a, b) => a.localeCompare(b, 'he'));
    }, [cities]);

    // Seed local state from `initial`, guarding against missing/invalid values.
    const seedCity = (initial.city || '').trim();
    const [city, setCity] = useState(seedCity || ALL_CITIES);
    const [mode, setMode] = useState(initial.mode === MODE_AVAIL ? MODE_AVAIL : MODE_BOOK);
    const [time, setTime] = useState(times.includes(initial.time) ? initial.time : DEFAULT_TIME);
    const [date, setDate] = useState(
        dates.some((d) => d.value === initial.date) ? initial.date : (dates[0] ? dates[0].value : ''),
    );
    const [party, setParty] = useState(() => {
        const p = parseInt(initial.party, 10);
        return p >= 1 && p <= 20 ? p : DEFAULT_PARTY;
    });

    // Re-sync from `initial` whenever the URL params change (back/forward or an in-app nav to
    // a different /discover?...). Keyed ONLY on the primitive param values (NOT cityOptions),
    // so an async businesses-load can't clobber a user's in-progress selection.
    useEffect(() => {
        setCity((initial.city || '').trim() || ALL_CITIES);
        setMode(initial.mode === MODE_AVAIL ? MODE_AVAIL : MODE_BOOK);
        setTime(times.includes(initial.time) ? initial.time : DEFAULT_TIME);
        setDate(dates.some((d) => d.value === initial.date) ? initial.date : (dates[0] ? dates[0].value : ''));
        const p = parseInt(initial.party, 10);
        setParty(p >= 1 && p <= 20 ? p : DEFAULT_PARTY);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initial.city, initial.mode, initial.time, initial.date, initial.party]);

    const [cityOpen, setCityOpen] = useState(false);
    const cityWrapRef = useRef(null);

    // Close the city panel on outside-click / Escape.
    useEffect(() => {
        if (!cityOpen) return undefined;
        const onDown = (e) => {
            if (cityWrapRef.current && !cityWrapRef.current.contains(e.target)) setCityOpen(false);
        };
        const onKey = (e) => { if (e.key === 'Escape') setCityOpen(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [cityOpen]);

    const pickCity = (c) => { setCity(c); setCityOpen(false); };

    const onSubmit = (e) => {
        e.preventDefault();
        const params = new URLSearchParams();
        if (city && city !== ALL_CITIES) params.set('city', city);
        params.set('mode', mode);
        if (time) params.set('time', time);
        if (date) params.set('date', date);
        if (party) params.set('party', String(party));
        navigate(`/discover?${params.toString()}`);
    };

    return (
        <form className="ds-bar" role="search" onSubmit={onSubmit}>
            {/* CITY */}
            <div className="ds-city" ref={cityWrapRef}>
                <button
                    type="button"
                    className="ds-field ds-city-btn"
                    aria-haspopup="dialog"
                    aria-expanded={cityOpen}
                    onClick={() => setCityOpen((v) => !v)}
                >
                    <span className="ds-city-ico" aria-hidden="true"><Icon name="store" size={18} /></span>
                    <span className="ds-city-txt">{city === ALL_CITIES ? t('search.all_cities') : city}</span>
                    <span className="ds-caret" aria-hidden="true"><Icon name="chevron-down" size={16} /></span>
                </button>

                {cityOpen && (
                    <div className="ds-city-panel" role="dialog" aria-label={t('search.city_dialog_aria')}>
                        <div className="ds-city-panel-head">
                            <span>{t('search.pick_city')}</span>
                            <button type="button" className="ds-city-close" aria-label={t('search.city_close')} onClick={() => setCityOpen(false)}>
                                <Icon name="x" size={16} />
                            </button>
                        </div>
                        <div className="ds-city-grid">
                            <button
                                type="button"
                                className={`ds-city-opt${city === ALL_CITIES ? ' is-active' : ''}`}
                                onClick={() => pickCity(ALL_CITIES)}
                            >
                                {t('search.all_cities')}
                            </button>
                            {cityOptions.map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    className={`ds-city-opt${city === c ? ' is-active' : ''}`}
                                    onClick={() => pickCity(c)}
                                >
                                    {c}
                                </button>
                            ))}
                        </div>
                        {cityOptions.length === 0 && (
                            <p className="ds-city-empty">{t('search.city_empty')}</p>
                        )}
                    </div>
                )}
            </div>

            {/* MODE */}
            <div className="ds-mode" role="group" aria-label={t('search.mode_group')}>
                <button
                    type="button"
                    className={`ds-mode-btn${mode === MODE_BOOK ? ' is-active' : ''}`}
                    aria-pressed={mode === MODE_BOOK}
                    onClick={() => setMode(MODE_BOOK)}
                >
                    {t('search.mode_book')}
                </button>
                <button
                    type="button"
                    className={`ds-mode-btn${mode === MODE_AVAIL ? ' is-active' : ''}`}
                    aria-pressed={mode === MODE_AVAIL}
                    onClick={() => setMode(MODE_AVAIL)}
                >
                    {t('search.mode_available')}
                </button>
            </div>

            {/* TIME */}
            <label className="ds-field ds-select">
                <span className="ds-field-ico" aria-hidden="true"><Icon name="clock" size={18} /></span>
                <select aria-label={t('search.time_aria')} value={time} onChange={(e) => setTime(e.target.value)}>
                    {times.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <span className="ds-caret" aria-hidden="true"><Icon name="chevron-down" size={16} /></span>
            </label>

            {/* DATE */}
            <label className="ds-field ds-select">
                <span className="ds-field-ico" aria-hidden="true"><Icon name="calendar" size={18} /></span>
                <select aria-label={t('search.date_aria')} value={date} onChange={(e) => setDate(e.target.value)}>
                    {dates.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
                <span className="ds-caret" aria-hidden="true"><Icon name="chevron-down" size={16} /></span>
            </label>

            {/* PARTY */}
            <label className="ds-field ds-select">
                <span className="ds-field-ico" aria-hidden="true"><Icon name="users" size={18} /></span>
                <select aria-label={t('search.party_aria')} value={party} onChange={(e) => setParty(parseInt(e.target.value, 10))}>
                    {parties.map((n) => <option key={n} value={n}>{partyLabel(n, t)}</option>)}
                </select>
                <span className="ds-caret" aria-hidden="true"><Icon name="chevron-down" size={16} /></span>
            </label>

            {/* SUBMIT */}
            <button type="submit" className="ds-submit">
                <Icon name="search" size={18} />
                <span>{t('search.submit')}</span>
            </button>
        </form>
    );
}
